<template>
  <v-card
    :class="['truecar-vehicle-card', { 'dark-mode': darkMode }]"
    elevation="2"
    @click="handleCardClick"
  >
    <!-- Vehicle Image -->
    <div class="image-container">
      <v-carousel
        v-if="vehicle.images && vehicle.images.length > 0"
        height="180"
        hide-delimiters
        show-arrows-on-hover
        class="vehicle-carousel"
      >
        <v-carousel-item
          v-for="(image, i) in vehicle.images"
          :key="i"
        >
          <v-img
            :src="getImageSrc(image.image)"
            height="180"
            cover
            @error="handleImageError"
            class="vehicle-image"
          >
            <template v-slot:placeholder>
              <v-img
                :src="fallbackImage"
                height="180"
                cover
                class="vehicle-image"
              ></v-img>
            </template>
          </v-img>
        </v-carousel-item>
      </v-carousel>

      <!-- Fallback single image -->
      <v-img
        v-else
        :src="fallbackImage"
        height="180"
        cover
        class="vehicle-image"
      ></v-img>

      <!-- Save Button -->
      <v-btn
        icon
        small
        class="save-btn"
        @click.stop="toggleSave"
      >
        <v-icon :color="isSaved ? 'red' : 'white'" size="20" class="material-icons-round">
          {{ isSaved ? 'favorite' : 'favorite' }}
        </v-icon>
      </v-btn>

    </div>

    <!-- Vehicle Info -->
    <div class="card-body">
      <!-- Title -->
      <h3 class="vehicle-title">{{ getVehicleTitle() }}</h3>

      <!-- Vehicle Specs -->
      <div class="vehicle-specs">
        <div class="spec-item" v-if="vehicle.odometer">
          <v-icon x-small color="grey">mdi-speedometer</v-icon>
          <span>{{ formatMileage(vehicle.odometer) }}</span>
        </div>
        <div class="spec-item" v-if="vehicle.transmission">
          <v-icon x-small color="grey">mdi-car-shift-pattern</v-icon>
          <span>{{ vehicle.transmission }}</span>
        </div>
        <div class="spec-item" v-if="vehicle.exterior_color">
          <v-icon x-small color="grey">mdi-palette</v-icon>
          <span>{{ vehicle.exterior_color }}</span>
        </div>
        <div class="spec-item" v-if="vehicle.fuel_type">
          <v-icon x-small color="grey">mdi-gas-station</v-icon>
          <span>{{ vehicle.fuel_type }}</span>
        </div>
      </div>

      <!-- Price Section -->
      <div class="price-section">
        <div class="network-text">Available Through Our Network</div>
        <div class="price-display">{{ formatPrice(vehicle.amount) }}</div>
      </div>

      <!-- Dealer Info -->
      <div class="dealer-info">
        <v-icon x-small color="primary" class="material-icons-round">store</v-icon>
        <span class="dealer-name">{{ vehicle.dealer_name || 'ROUND TABLES Auto Sales' }}</span>
      </div>
      <div class="dealer-address">
        <v-icon x-small color="primary" class="material-icons-round">location_on</v-icon>
        {{ getLocationText() || '931 N Dysart Rd Ste B, Avondale, AZ 85323' }}
      </div>

      <!-- Action Buttons -->
      <div class="action-buttons">
        <v-btn
          small
          depressed
          class="view-btn"
          @click.stop="viewDetails"
        >
          VIEW
        </v-btn>
        <v-btn
          outlined
          small
          :loading="expressingInterest"
          class="interested-btn"
          @click.stop="expressInterest"
        >
          INTERESTED IN
        </v-btn>
      </div>
    </div>

    <!-- Dealership Info Modal -->
    <v-dialog v-model="showDealershipModal" max-width="450" persistent>
      <v-card class="dealership-modal">
        <v-card-title class="dealership-modal-header">
          <v-icon class="material-icons-round mr-2" color="white">store</v-icon>
          Dealership Information
          <v-spacer></v-spacer>
          <v-btn icon small dark @click="showDealershipModal = false">
            <v-icon class="material-icons-round">close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-6">
          <div class="dealership-info-content">
            <div class="dealership-name-large">{{ dealershipInfo.name }}</div>

            <div class="dealership-detail-row">
              <v-icon class="material-icons-round" small color="primary">location_on</v-icon>
              <span>{{ dealershipInfo.full_address || dealershipInfo.address }}</span>
            </div>

            <div class="dealership-detail-row">
              <v-icon class="material-icons-round" small color="primary">phone</v-icon>
              <a :href="'tel:' + dealershipInfo.phone" class="phone-link">{{ dealershipInfo.phone }}</a>
            </div>

            <v-divider class="my-4"></v-divider>

            <div class="vehicle-summary">
              <div class="vehicle-summary-title">Vehicle of Interest</div>
              <div class="vehicle-summary-detail">{{ getVehicleTitle() }}</div>
              <div class="vehicle-summary-price">{{ formatPrice(vehicle.amount) }}</div>
            </div>
          </div>
        </v-card-text>

        <v-card-actions class="pa-4 pt-0">
          <v-btn
            block
            large
            color="success"
            dark
            :href="'tel:' + dealershipInfo.phone"
          >
            <v-icon left class="material-icons-round">phone</v-icon>
            Call Now
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Payment Calculator Modal -->
    <v-dialog v-model="showPaymentCalculator" max-width="500" persistent>
      <v-card>
        <v-card-title class="text-h6 font-weight-bold">
          Payment Calculator
        </v-card-title>

        <v-card-text>
          <div class="mb-4">
            <h4 class="mb-2">{{ getVehicleTitle() }}</h4>
            <div class="text-h6 font-weight-bold text--primary">
              {{ formatPrice(vehicle.amount) }}
            </div>
            <div class="text-caption text--secondary mt-2">
              Default values are set by admin. Customize to see how different terms affect your payment.
            </div>
          </div>

          <v-form ref="calculatorForm">
            <!-- APR Rate -->
            <v-text-field
              v-model="calculator.aprRate"
              label="APR Rate (%)"
              type="number"
              step="0.01"
              min="0"
              max="30"
              outlined
              dense
              suffix="%"
              @input="calculatePayment"
              :rules="[v => !!v || 'APR is required', v => v >= 0 || 'APR must be positive', v => v <= 30 || 'APR must be 30% or less']"
            ></v-text-field>

            <!-- Down Payment -->
            <v-text-field
              v-model="calculator.downPayment"
              label="Down Payment"
              type="number"
              min="0"
              outlined
              dense
              prefix="$"
              @input="calculatePayment"
              :rules="[v => v >= 0 || 'Down payment must be positive', v => v <= vehiclePrice || 'Down payment cannot exceed vehicle price']"
            ></v-text-field>

            <!-- Loan Term (User-editable dropdown) -->
            <v-select
              v-model="calculator.termMonths"
              :items="loanTermOptions"
              label="Loan Term"
              outlined
              dense
              @change="calculatePayment"
              :rules="[v => !!v || 'Loan term is required']"
            ></v-select>

            <!-- Calculated Payment Display -->
            <v-card class="mt-4 pa-4" color="green lighten-5" outlined>
              <div class="text-center">
                <div class="text-h4 font-weight-bold green--text">
                  ${{ calculatedPayment }}/mo
                </div>
                <div class="text-subtitle-2 green--text">
                  Estimated Monthly Payment
                </div>
              </div>
            </v-card>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-btn text @click="resetCalculator">
            Reset
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn text @click="closePaymentCalculator">
            Cancel
          </v-btn>
          <v-btn color="green" dark @click="applyCalculatedPayment">
            Apply
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import Vue from 'vue'

export default {
  name: 'TrueCarVehicleCard',
  props: {
    vehicle: {
      type: Object,
      required: true
    },
    darkMode: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      fallbackImage: require('@/assets/img/coming-soon.jpg'),
      brokenImages: new Set(),
      isSaved: false,
      showPaymentCalculator: false,
      customPayment: null,
      calculator: {
        aprRate: null,
        downPayment: 0,
        termMonths: null
      },
      calculatedPayment: 0,
      loanTermOptions: [
        { text: '36 months', value: 36 },
        { text: '48 months', value: 48 },
        { text: '60 months', value: 60 },
        { text: '72 months', value: 72 },
        { text: '84 months', value: 84 },
        { text: '96 months', value: 96 }
      ],
      // Vehicle interest tracking
      expressingInterest: false,
      showDealershipModal: false,
      dealershipInfo: {
        name: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        full_address: ''
      }
    }
  },
  computed: {
    vehiclePrice() {
      return parseFloat(this.vehicle?.amount || this.vehicle?.price || 0)
    }
  },
  mounted() {
    // Initialize calculator defaults when component mounts
    this.initializeCalculatorDefaults()
  },
  methods: {
    getImageSrc(imageSrc) {
      if (!imageSrc || this.brokenImages.has(imageSrc)) {
        return this.fallbackImage
      }
      
      if (imageSrc.startsWith('http') || imageSrc.startsWith('data:image')) {
        return imageSrc
      }
      
      return this.fallbackImage
    },

    handleImageError(event) {
      const originalSrc = event.target.src
      if (originalSrc && !originalSrc.includes('coming-soon.jpg')) {
        this.brokenImages.add(originalSrc)
      }
      event.target.src = this.fallbackImage
    },

    getVehicleTitle() {
      if (!this.vehicle) return 'Vehicle'
      
      const parts = []
      if (this.vehicle.year) parts.push(this.vehicle.year)
      if (this.vehicle.make) parts.push(this.vehicle.make)
      if (this.vehicle.model) parts.push(this.vehicle.model)
      if (this.vehicle.trim) parts.push(this.vehicle.trim)
      
      return parts.join(' ') || 'Vehicle'
    },

    formatPrice(price) {
      if (!price) return 'Call for pricing'

      const numPrice = parseFloat(price)
      if (isNaN(numPrice) || numPrice <= 0) return 'Call for pricing'

      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(numPrice)
    },

    formatMileage(mileage) {
      if (!mileage) return 'N/A'

      const numMileage = parseInt(mileage)
      if (isNaN(numMileage)) return 'N/A'

      return new Intl.NumberFormat('en-US').format(numMileage) + ' Miles'
    },

    getBadgeClass() {
      // Determine badge class based on vehicle status
      const status = this.vehicle.status || 'available'
      if (status === 'sold') return 'badge-sold'
      if (status === 'pending') return 'badge-pending'
      return 'badge-search'
    },

    getBadgeText() {
      // Determine badge text based on vehicle status
      const status = this.vehicle.status || 'available'
      if (status === 'sold') return 'Main Sold'
      if (status === 'pending') return 'Pending'
      return 'Main Search'
    },

    getLocationText() {
      // Build location string from available fields
      const city = this.vehicle.city || this.vehicle.dealer_location || ''
      const state = this.vehicle.state || ''
      if (city && state) return `${city}, ${state}`
      return city || state || ''
    },

    toggleSave() {
      this.isSaved = !this.isSaved
      // TODO: Implement save functionality
      console.log('Toggle save vehicle:', this.vehicle.id)
    },

    contactDealer() {
      // Open Contact Dealer AI Assistant modal
      this.$emit('contact-dealer', this.vehicle)
    },

    // Handle card click - track and emit event
    handleCardClick() {
      this.trackVehicleClick()
      this.$emit('vehicle-clicked', this.vehicle.id)
    },

    viewDetails() {
      // Track the click before navigating
      this.trackVehicleClick()
      this.$emit('vehicle-clicked', this.vehicle.id)
    },

    // Track vehicle click for analytics
    async trackVehicleClick() {
      const leadId = localStorage.getItem('inventory_lead_id')

      // Only track if user has submitted lead form
      if (!leadId) {
        console.log('📊 Vehicle click not tracked - no lead ID')
        return
      }

      try {
        await axios.post('/api/vehicle-interest/track-click', {
          user_id: leadId,
          vehicle_id: this.vehicle.id,
          domain: window.location.hostname
        })
        console.log('📊 Vehicle click tracked:', this.vehicle.id)
      } catch (error) {
        // Silent fail - don't block user navigation
        console.error('Failed to track vehicle click:', error)
      }
    },

    // Express interest in vehicle - triggers dealership notification
    async expressInterest() {
      console.log('🚗 expressInterest() called for vehicle:', this.vehicle.id)

      // Check if user is a lead (has inventory_lead_id in localStorage)
      const leadId = localStorage.getItem('inventory_lead_id')
      console.log('🔑 Lead ID from localStorage:', leadId)

      if (!leadId) {
        // User hasn't submitted lead form yet - show message
        console.log('⚠️ No lead ID found, emitting lead-required event')
        this.$emit('lead-required', this.vehicle)
        // Also show a toast message
        Vue.$toast.info('Please submit your contact information first to express interest.', {
          position: 'top-right',
          duration: 5000
        })
        return
      }

      this.expressingInterest = true
      console.log('📤 Sending express interest request...')

      try {
        const response = await axios.post('/api/vehicle-interest/express-interest', {
          user_id: leadId,
          vehicle_id: this.vehicle.id,
          domain: window.location.hostname
        })

        console.log('📥 Response received:', response.data)

        if (response.data.flag === 1) {
          // Store dealership info and show modal
          this.dealershipInfo = response.data.data.dealership
          this.showDealershipModal = true

          console.log('✅ Interest expressed successfully:', response.data)
        } else {
          console.error('Failed to express interest:', response.data.message)
          Vue.$toast.error(response.data.message || 'Failed to express interest', {
            position: 'top-right'
          })
        }
      } catch (error) {
        console.error('❌ Error expressing interest:', error)
        console.error('Error details:', error.response?.data || error.message)
        Vue.$toast.error('Failed to express interest. Please try again.', {
          position: 'top-right'
        })
      } finally {
        this.expressingInterest = false
      }
    },

    // Initialize calculator defaults
    initializeCalculatorDefaults() {
      // Set default values without opening the calculator
      this.calculator.aprRate = this.vehicle.loan_apr_rate || 6.70
      this.calculator.downPayment = 0

      // Ensure loan term matches one of the dropdown options
      const defaultTerm = this.vehicle.loan_term_months || 84
      const validTerm = this.loanTermOptions.find(option => option.value === defaultTerm)
      this.calculator.termMonths = validTerm ? defaultTerm : 84

      console.log('🔧 Payment Calculator - Initialized defaults:', {
        aprRate: this.calculator.aprRate,
        termMonths: this.calculator.termMonths,
        adminTerm: this.vehicle.loan_term_months,
        validTerm: !!validTerm
      })
    },

    // Payment Calculator Methods
    openPaymentCalculator() {
      // Ensure defaults are set (in case vehicle data changed)
      this.initializeCalculatorDefaults()

      // Calculate initial payment with current values
      this.calculatePayment()

      console.log('🔧 Payment Calculator - Opening with values:', {
        aprRate: this.calculator.aprRate,
        termMonths: this.calculator.termMonths,
        downPayment: this.calculator.downPayment,
        vehiclePrice: this.vehiclePrice
      })

      this.showPaymentCalculator = true
    },

    closePaymentCalculator() {
      this.showPaymentCalculator = false
    },

    calculatePayment() {
      try {
        const principal = this.vehiclePrice - (parseFloat(this.calculator.downPayment) || 0)
        const monthlyRate = (parseFloat(this.calculator.aprRate) || 0) / 100 / 12
        const numPayments = parseInt(this.calculator.termMonths) || 84

        if (principal <= 0) {
          this.calculatedPayment = '0.00'
          return
        }

        if (monthlyRate === 0) {
          // No interest case
          this.calculatedPayment = (principal / numPayments).toFixed(2)
          return
        }

        // Standard loan payment formula: P * [r(1+r)^n] / [(1+r)^n - 1]
        const monthlyPayment = principal * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / (Math.pow(1 + monthlyRate, numPayments) - 1)

        this.calculatedPayment = monthlyPayment.toFixed(2)
      } catch (error) {
        console.error('Error calculating payment:', error)
        this.calculatedPayment = '0.00'
      }
    },

    applyCalculatedPayment() {
      // Update the custom payment to show on the card
      this.customPayment = this.calculatedPayment
      this.closePaymentCalculator()
    },

    resetCalculator() {
      // Reset to original admin defaults using the initialization method
      this.initializeCalculatorDefaults()

      console.log('🔄 Payment Calculator - Reset to admin defaults')

      this.calculatePayment()
      this.customPayment = null
    }
  }
}
</script>

<style scoped>
/* Professional Vehicle Card Design */
.truecar-vehicle-card {
  background: rgba(255, 255, 255, 0.82) !important;
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  border-radius: 12px !important;
  border: 1px solid rgba(255, 255, 255, 0.25) !important;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.12);
  transition: all 0.3s ease;
  cursor: pointer;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.truecar-vehicle-card:hover {
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15) !important;
  transform: translateY(-4px);
}

/* Image Container */
.image-container {
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e8ed 100%);
}

.vehicle-carousel {
  border-radius: 12px 12px 0 0;
}

.vehicle-image {
  transition: transform 0.4s ease;
}

.truecar-vehicle-card:hover .vehicle-image {
  transform: scale(1.05);
}

.save-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  background: rgba(0, 0, 0, 0.4) !important;
  backdrop-filter: blur(4px);
  z-index: 2;
  width: 36px !important;
  height: 36px !important;
}

.save-btn:hover {
  background: rgba(0, 0, 0, 0.6) !important;
}

/* Card Body */
.card-body {
  padding: 16px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.vehicle-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #0f172a;
  text-shadow: 0 1px 2px rgba(255,255,255,0.5);
  margin: 0;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Vehicle Specs */
.vehicle-specs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.spec-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.75rem;
  color: #0f172a;
  font-weight: 600;
  background: rgba(255,255,255,0.45);
  padding: 4px 8px;
  border-radius: 4px;
}

.spec-item .v-icon {
  opacity: 0.7;
}

/* Price Section */
.price-section {
  margin-top: 4px;
  padding-top: 10px;
  border-top: 1px solid #f0f2f5;
}

.network-text {
  font-size: 0.75rem;
  color: #374151;
  text-decoration: underline;
  margin-bottom: 4px;
}

.price-display {
  font-size: 1.5rem;
  font-weight: 700;
  color: #0f172a;
  text-shadow: 0 1px 2px rgba(255,255,255,0.5);
}

/* Dealer Info */
.dealer-info {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8rem;
  color: #1e293b;
  padding-top: 8px;
  border-top: 1px solid rgba(0,0,0,0.1);
}

.dealer-name {
  font-weight: 600;
  color: #0f172a;
}

.dealer-location {
  color: #334155;
}

.dealer-address {
  font-size: 0.75rem;
  color: #374151;
  margin-top: 2px;
  line-height: 1.4;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  gap: 8px;
  margin-top: auto;
  padding-top: 12px;
}

.view-btn {
  flex: 1;
  height: 32px !important;
  border-radius: 4px !important;
  text-transform: uppercase !important;
  font-weight: 600 !important;
  font-size: 0.75rem !important;
  letter-spacing: 0.5px;
  background: #5a6a7a !important;
  color: white !important;
}

.view-btn:hover {
  background: #4a5a6a !important;
}

.interested-btn {
  flex: 1.5;
  height: 32px !important;
  border-radius: 4px !important;
  text-transform: uppercase !important;
  font-weight: 600 !important;
  font-size: 0.75rem !important;
  letter-spacing: 0.5px;
  border: 1px solid #c0c4c8 !important;
  background: white !important;
  color: #4a5a6a !important;
}

.interested-btn:hover {
  background: #f5f7fa !important;
  border-color: #9a9ea2 !important;
}

/* Responsive Design */
@media (max-width: 768px) {
  .card-body {
    padding: 14px;
    gap: 10px;
  }

  .vehicle-title {
    font-size: 0.95rem;
  }

  .vehicle-specs {
    gap: 6px;
  }

  .spec-item {
    font-size: 0.7rem;
    padding: 3px 6px;
  }

  .action-buttons {
    flex-direction: column;
    gap: 8px;
  }

  .view-btn,
  .interested-btn {
    flex: none;
    width: 100%;
  }

  .price-badge {
    font-size: 1rem;
    padding: 6px 10px;
  }
}

@media (max-width: 600px) {
  .card-body {
    padding: 12px;
    gap: 8px;
  }

  .vehicle-title {
    font-size: 0.9rem;
  }

  .dealer-info {
    font-size: 0.75rem;
    padding-top: 8px;
  }
}

/* Interest Button */
.interest-btn {
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 0.85rem !important;
  letter-spacing: 0.01em;
}

/* Dealership Modal Styles */
.dealership-modal {
  border-radius: 12px !important;
  overflow: hidden;
}

.dealership-modal-header {
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
  color: white !important;
  padding: 16px 20px !important;
}

.dealership-info-content {
  text-align: center;
}

.dealership-name-large {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1976d2;
  margin-bottom: 20px;
}

.dealership-detail-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 12px;
  font-size: 1rem;
  color: #374151;
}

.phone-link {
  color: #1976d2;
  text-decoration: none;
  font-weight: 600;
}

.phone-link:hover {
  text-decoration: underline;
}

.vehicle-summary {
  background: #f8fafc;
  border-radius: 8px;
  padding: 16px;
  margin-top: 16px;
}

.vehicle-summary-title {
  font-size: 0.75rem;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 8px;
}

.vehicle-summary-detail {
  font-size: 1.1rem;
  font-weight: 600;
  color: #111827;
}

.vehicle-summary-price {
  font-size: 1.25rem;
  font-weight: 700;
  color: #10b981;
  margin-top: 4px;
}

/* ==========================================
   DARK MODE STYLES
   ========================================== */
.dark-mode .truecar-vehicle-card {
  background: rgba(30, 21, 53, 0.88) !important;
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  border: 1px solid rgba(124, 92, 255, 0.4) !important;
}

.dark-mode .truecar-vehicle-card:hover {
  border-color: #7c5cff !important;
  box-shadow: 0 8px 32px rgba(124, 92, 255, 0.4) !important;
}

.dark-mode .card-body {
  background: transparent !important;
}

.dark-mode .vehicle-title {
  color: #ffffff !important;
}

.dark-mode .spec-item {
  background: rgba(124, 92, 255, 0.3) !important;
  color: #ffffff !important;
  border: 1px solid rgba(124, 92, 255, 0.5) !important;
}

.dark-mode .network-text {
  color: #ffffff !important;
}

.dark-mode .price-section {
  border-top-color: rgba(124, 92, 255, 0.4) !important;
}

.dark-mode .price-display {
  color: #10b981 !important;
}

.dark-mode .dealer-info {
  border-top-color: rgba(124, 92, 255, 0.4) !important;
}

.dark-mode .dealer-name {
  color: #ffffff !important;
}

.dark-mode .dealer-location {
  color: #ffffff !important;
}

.dark-mode .view-btn {
  background: #7c5cff !important;
  color: #ffffff !important;
}

.dark-mode .interested-btn {
  background: transparent !important;
  border: 1px solid rgba(124, 92, 255, 0.4) !important;
  color: #ffffff !important;
}

.dark-mode .interested-btn:hover {
  background: rgba(124, 92, 255, 0.2) !important;
  border-color: #7c5cff !important;
}

.dark-mode .image-container {
  background: linear-gradient(135deg, #251a45 0%, #1a1230 100%) !important;
}

.dark-mode .vehicle-summary-title {
  color: #b0a8c9 !important;
}

.dark-mode .vehicle-summary-detail {
  color: #ffffff !important;
}
</style>
